import React, { useMemo, useState } from 'react';
import { Alert, Box, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { AnimatedPage, AnimatedSection, metricMotion } from '../components/animations/MotionContainers';
import { SummaryMetricCard } from '../components/dashboard/SummaryMetricCard';
import { WarehouseComparisonTable } from '../components/dashboard/WarehouseComparisonTable';
import { useFlujosCatalogs } from '../hooks/useFlujosCatalogs';
import { useFlujosEntradas } from '../hooks/useFlujosEntradas';
import {
  formatDisplayDate,
  getCampaignOptionsFromEntries,
  getCurrentCampaignRange,
} from '../utils/dashboardData';
import {
  calculateFlowTotals,
  formatKilos,
  formatPercent,
  groupTotalsByWarehouse,
} from '../utils/flujoCalculations';
import { isDateInRange } from '../utils/dateFormatters';

const filterResultsMotion = {
  hidden: {
    opacity: 0,
    y: 12,
    filter: 'blur(2px)',
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.045,
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    filter: 'blur(1px)',
    transition: {
      duration: 0.16,
      ease: 'easeIn',
    },
  },
};

export const ComparativaAlmacenes = () => {
  const reduceMotion = useReducedMotion();
  const {
    almacenes,
    cajasPalet,
    loading: catalogsLoading,
    error: catalogsError,
  } = useFlujosCatalogs();
  const { rows: entradas, loading: entradasLoading, error: entradasError } = useFlujosEntradas({
    limit: 500,
    sortBy: 'fecha',
    sortDirection: 'desc',
  }, { almacenes, cajasPalet });
  const currentCampaignStartYear = getCurrentCampaignRange().startYear;
  const campaignOptions = useMemo(
    () => getCampaignOptionsFromEntries(entradas),
    [entradas]
  );
  const [selectedCampaignStartYear, setSelectedCampaignStartYear] = useState(currentCampaignStartYear);
  const selectedCampaign = useMemo(
    () => campaignOptions.find((campaign) => campaign.startYear === selectedCampaignStartYear) ||
      getCurrentCampaignRange(),
    [campaignOptions, selectedCampaignStartYear]
  );
  const campaignRows = useMemo(
    () => entradas.filter((row) => (
      isDateInRange(row.fecha, selectedCampaign.startValue, selectedCampaign.endValue)
    )),
    [entradas, selectedCampaign]
  );
  const totals = calculateFlowTotals(campaignRows);
  const comparisonRows = groupTotalsByWarehouse(campaignRows, almacenes);
  const bestPerformance = comparisonRows.reduce(
    (best, row) => (row.rendimiento > best.rendimiento ? row : best),
    { almacen: 'Sin datos', rendimiento: 0 }
  );

  return (
    <AnimatedPage>
        <Stack spacing={2.5}>
          <AnimatedSection>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  <CompareArrowsRoundedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h1">Comparativa de almacenes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Comparacion operativa entre Betxi, Alquerias y Ulldecona
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  minWidth: { xs: '100%', sm: 270 },
                }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <CalendarMonthOutlinedIcon color="primary" fontSize="medium" />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ color: 'primary.main', fontSize: 12, fontWeight: 900, lineHeight: '15px' }}>
                      Campaña
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: '14px' }}>
                      {formatDisplayDate(selectedCampaign.startValue)} - {formatDisplayDate(selectedCampaign.endValue)}
                    </Typography>
                  </Box>
                  <TextField
                    select
                    size="small"
                    value={selectedCampaignStartYear}
                    onChange={(event) => setSelectedCampaignStartYear(Number(event.target.value))}
                    sx={{ minWidth: 112 }}
                  >
                    {campaignOptions.map((campaign) => (
                      <MenuItem key={campaign.startYear} value={campaign.startYear}>
                        {campaign.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Box>
            </Stack>
          </AnimatedSection>

          {(entradasLoading || catalogsLoading || entradasError || catalogsError) && (
            <Alert severity={entradasError || catalogsError ? 'error' : 'info'} variant="outlined">
              {entradasError || catalogsError || 'Cargando datos...'}
            </Alert>
          )}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={selectedCampaignStartYear}
              initial={reduceMotion ? false : 'hidden'}
              animate={reduceMotion ? undefined : 'visible'}
              exit={reduceMotion ? undefined : 'exit'}
              variants={reduceMotion ? undefined : filterResultsMotion}
            >
              <Stack spacing={2.5}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<WarehouseOutlinedIcon />}
                        title="Almacenes"
                        value={almacenes.length}
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<ScaleOutlinedIcon />}
                        title="Kilos volcados"
                        value={formatKilos(totals.kilosVolcados)}
                        suffix="kg"
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<Inventory2OutlinedIcon />}
                        title="Kilos clasificados"
                        value={formatKilos(totals.kilosClasificados)}
                        suffix="kg"
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<PercentOutlinedIcon />}
                        title="Rendimiento global"
                        value={formatPercent(totals.rendimiento)}
                        suffix="%"
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<FactCheckOutlinedIcon />}
                        title="Mejor rendimiento"
                        value={bestPerformance.almacen}
                      />
                    </AnimatedSection>
                  </Grid>
                </Grid>

                <AnimatedSection>
                  <WarehouseComparisonTable rows={comparisonRows} />
                </AnimatedSection>
              </Stack>
            </motion.div>
          </AnimatePresence>
        </Stack>
    </AnimatedPage>
  );
};
